import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import toast from 'react-hot-toast'
import { useAuth } from '../../context/AuthContext'
import { authService } from '../../services/authService'
import { companyService } from '../../services/companyService'
import {
  Menu,
  Bell,
  Search,
  User,
  Settings,
  LogOut,
  ChevronDown
} from 'lucide-react'

const Header = ({ onMenuClick }) => {
  const navigate = useNavigate()
  const [isProfileOpen, setIsProfileOpen] = useState(false)
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false)
  const { user, logout } = useAuth()
  const [companyName, setCompanyName] = useState(null)
  const [vendorName, setVendorName] = useState(null)

  // Fetch company name for company users
  useEffect(() => {
    const fetchCompanyName = async () => {
      if (user?.role === 'company' && (user?.id || user?.user_id)) {
        try {
          // Check if company_name is already in user object
          if (user.company_name) {
            setCompanyName(user.company_name)
            return
          }

          // Try to get from getCurrentUser which might have company data
          const userData = await authService.getCurrentUser()
          if (userData?.company_name) {
            setCompanyName(userData.company_name)
          } else if (userData?.company_details?.company_name) {
            setCompanyName(userData.company_details.company_name)
          } else {
            // Fallback: fetch full company profile
            const userId = user.id || user.user_id
            const response = await companyService.getCompanyProfile(userId)
            if (response.success && response.data?.company_details?.company_name) {
              setCompanyName(response.data.company_details.company_name)
            }
          }
        } catch (error) {
          console.error('Error fetching company name:', error)
        }
      }
    }

    fetchCompanyName()
  }, [user])

  // Fetch vendor name for vendor users
  useEffect(() => {
    const fetchVendorName = async () => {
      if (user?.role === 'vendor' && (user?.id || user?.user_id)) {
        try {
          // Check if vendor_name or company_name is already in user object
          if (user.vendor_name) {
            setVendorName(user.vendor_name)
            return
          }
          if (user.company_name) {
            setVendorName(user.company_name)
            return
          }

          // Try to get from getCurrentUser which might have vendor data
          const userData = await authService.getCurrentUser()
          if (userData?.vendor_name) {
            setVendorName(userData.vendor_name)
          } else if (userData?.company_name) {
            setVendorName(userData.company_name)
          } else if (userData?.vendor_details?.vendor_name) {
            setVendorName(userData.vendor_details.vendor_name)
          } else if (userData?.vendor_details?.company_name) {
            setVendorName(userData.vendor_details.company_name)
          } else {
            // Fallback: fetch full vendor profile
            const userId = user.id || user.user_id
            const response = await authService.getVendorProfile(userId)
            if (response.success && response.data) {
              // Check various possible locations for vendor name
              const vendorData = response.data
              if (vendorData.vendor_name) {
                setVendorName(vendorData.vendor_name)
              } else if (vendorData.company_name) {
                setVendorName(vendorData.company_name)
              } else if (vendorData.legal_entity_name) {
                setVendorName(vendorData.legal_entity_name)
              } else if (vendorData.vendor_details?.vendor_name) {
                setVendorName(vendorData.vendor_details.vendor_name)
              } else if (vendorData.vendor_details?.company_name) {
                setVendorName(vendorData.vendor_details.company_name)
              }
            }
          }
        } catch (error) {
          console.error('Error fetching vendor name:', error)
        }
      }
    }

    fetchVendorName()
  }, [user])

  const notifications = [
    {
      id: 1,
      title: 'New project match',
      message: 'You have a new project that matches your skills',
      time: '2 minutes ago',
      unread: true
    },
    {
      id: 2,
      title: 'Profile update required',
      message: 'Please update your profile to improve matching',
      time: '1 hour ago',
      unread: true
    },
    {
      id: 3,
      title: 'Payment received',
      message: 'Payment of $2,500 has been processed',
      time: '3 hours ago',
      unread: false
    }
  ]

  const handleLogout = async () => {
    try {
      await authService.logout()
      logout()
      toast.success('Logged out successfully')
      navigate('/login')
    } catch (error) {
      console.error('Logout error:', error)
      toast.error('Error logging out')
    } finally {
      setIsProfileOpen(false)
    }
  }

  return (
    <header className="bg-white/80 backdrop-blur-sm border-b border-secondary-200 sticky top-0 z-40 dark:bg-secondary-900/80 dark:border-secondary-800">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Left side */}
          <div className="flex items-center">
            <button
              onClick={onMenuClick}
              className="lg:hidden p-2 rounded-lg text-secondary-600 hover:bg-secondary-100 dark:text-secondary-300 dark:hover:bg-secondary-800 transition-colors"
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Search */}
            <div className="hidden md:block ml-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-secondary-400 dark:text-secondary-500" />
                <input
                  type="text"
                  placeholder="Search..."
                  className="pl-10 pr-4 py-2 w-64 bg-secondary-50 border border-secondary-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent dark:bg-secondary-800 dark:border-secondary-700 dark:text-secondary-100 dark:placeholder-secondary-400"
                />
              </div>
            </div>
          </div>

          {/* Right side */}
          <div className="flex items-center space-x-4">
            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => setIsNotificationsOpen(!isNotificationsOpen)}
                className="p-2 rounded-lg text-secondary-600 hover:bg-secondary-100 dark:text-secondary-300 dark:hover:bg-secondary-800 transition-colors relative"
              >
                <Bell className="w-5 h-5" />
                {notifications.some(n => n.unread) && (
                  <span className="absolute -top-1 -right-1 w-3 h-3 bg-error-500 rounded-full"></span>
                )}
              </button>

              {isNotificationsOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute right-0 mt-2 w-80 bg-white dark:bg-secondary-800 rounded-lg shadow-lg border border-secondary-200 dark:border-secondary-700 z-50"
                >
                  <div className="p-4 border-b border-secondary-200 dark:border-secondary-700">
                    <h3 className="font-semibold text-secondary-900 dark:text-secondary-100">Notifications</h3>
                  </div>
                  <div className="max-h-80 overflow-y-auto">
                    {notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={`p-4 border-b border-secondary-100 dark:border-secondary-700 hover:bg-secondary-50 dark:hover:bg-secondary-700 cursor-pointer ${notification.unread ? 'bg-primary-50 dark:bg-primary-900/20' : ''
                          }`}
                      >
                        <div className="flex items-start space-x-3">
                          <div className={`w-2 h-2 rounded-full mt-2 ${notification.unread ? 'bg-primary-500' : 'bg-transparent'
                            }`} />
                          <div className="flex-1">
                            <h4 className="text-sm font-medium text-secondary-900 dark:text-secondary-100">
                              {notification.title}
                            </h4>
                            <p className="text-sm text-secondary-600 dark:text-secondary-300 mt-1">
                              {notification.message}
                            </p>
                            <p className="text-xs text-secondary-500 dark:text-secondary-400 mt-1">
                              {notification.time}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="p-4 border-t border-secondary-200 dark:border-secondary-700">
                    <Link
                      to="/notifications"
                      className="text-sm text-primary-600 hover:text-primary-500 dark:text-primary-400 dark:hover:text-primary-300 font-medium"
                    >
                      View all notifications
                    </Link>
                  </div>
                </motion.div>
              )}
            </div>

            {/* Profile Dropdown */}
            <div className="relative">
              <button
                onClick={() => setIsProfileOpen(!isProfileOpen)}
                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-secondary-100 dark:hover:bg-secondary-800 transition-colors"
              >
                <div className="w-8 h-8 bg-gradient-to-r from-primary-600 to-accent-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {user?.role === 'company'
                      ? (companyName?.charAt(0) || user?.company_name?.charAt(0) || user?.firstName?.charAt(0) || 'C').toUpperCase()
                      : user?.role === 'vendor'
                        ? (vendorName?.charAt(0) || user?.vendor_name?.charAt(0) || user?.company_name?.charAt(0) || user?.firstName?.charAt(0) || 'V').toUpperCase()
                        : (user?.firstName?.charAt(0) || 'U').toUpperCase()}
                  </span>
                </div>
                <div className="hidden md:block text-left">
                  <p className="text-sm font-medium text-secondary-900 dark:text-secondary-100">
                    {user?.role === 'company'
                      ? (companyName || user?.company_name || user?.firstName || 'Company')
                      : user?.role === 'vendor'
                        ? (vendorName || user?.vendor_name || user?.company_name || `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Vendor')
                        : `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'User'}
                  </p>
                  <p className="text-xs text-secondary-500 dark:text-secondary-400 capitalize">
                    {user?.role || 'User'}
                  </p>
                </div>
                <ChevronDown className="w-4 h-4 text-secondary-400 dark:text-secondary-500" />
              </button>

              {isProfileOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute right-0 mt-2 w-56 bg-white dark:bg-secondary-800 rounded-lg shadow-lg border border-secondary-200 dark:border-secondary-700 z-50"
                >
                  <div className="p-4 border-b border-secondary-200 dark:border-secondary-700">
                    <p className="text-sm font-medium text-secondary-900 dark:text-secondary-100">
                      {user?.role === 'vendor' && vendorName
                        ? vendorName
                        : user?.role === 'company' && companyName
                          ? companyName
                          : `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'User'}
                    </p>
                    <p className="text-xs text-secondary-500 dark:text-secondary-400">{user?.email}</p>
                  </div>

                  <div className="py-2">
                    <Link
                      to={
                        user?.role === 'company'
                          ? '/dashboard/company/profile'
                          : user?.role === 'vendor'
                            ? '/dashboard/vendor/profile'
                            : '/profile'
                      }
                      className="flex items-center space-x-3 px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-50 dark:text-secondary-200 dark:hover:bg-secondary-700 transition-colors"
                      onClick={() => setIsProfileOpen(false)}
                    >
                      <User className="w-4 h-4" />
                      <span>Profile</span>
                    </Link>

                    {/* <Link
                        to="/settings"
                        className="flex items-center space-x-3 px-4 py-2 text-sm text-secondary-700 hover:bg-secondary-50 dark:text-secondary-200 dark:hover:bg-secondary-700 transition-colors"
                        onClick={() => setIsProfileOpen(false)}
                      >
                        <Settings className="w-4 h-4" />
                        <span>Settings</span>
                      </Link> */}
                  </div>

                  <div className="border-t border-secondary-200 dark:border-secondary-700 py-2">
                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-3 px-4 py-2 text-sm text-error-600 hover:bg-error-50 dark:text-error-400 dark:hover:bg-error-900/20 transition-colors w-full text-left"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Sign out</span>
                    </button>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}

export default Header
